import {
  Ban,
  Calendar,
  Check,
  CheckCheck,
  DollarSign,
  FileText,
  ListChecks,
} from 'lucide-react';
import {
  memo,
  useCallback,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { absoluteFileUrl } from '@/entities/message';
import type { Message } from '@/entities/message';
import { cn } from '@/shared/lib/cn';
import { formatTime } from '@/shared/lib/format';

export type MessageBubbleProps = {
  message: Message;
  isOwn: boolean;
  onOpenMedia?: (media: { url: string; name: string | null; type: string | null }) => void;
  // Selection mode (estilo WhatsApp). Quando ativo, qualquer tap no bubble
  // deve adicionar/remover da selecao em vez de abrir media etc. Long-press
  // entra no modo (chamando onLongPress).
  selectionMode?: boolean;
  isSelected?: boolean;
  onLongPress?: (messageId: string) => void;
  onSelectToggle?: (messageId: string) => void;
};

type CtaReuniao = { titulo: string; data: string; hora: string; local: string };
type CtaValor = { valor: number; descricao: string };
type CtaProximo = { descricao: string; dueDate: string };

const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE_PX = 8;

const formatBRL = (cents: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(cents);

const parseJson = <T,>(content: string): T | null => {
  try {
    return JSON.parse(content) as T;
  } catch {
    return null;
  }
};

// Status checkmark estilo iMessage/WhatsApp -- apenas para mensagens do
// usuario. Um check (cinza) = entregue ao servidor. Dois checks (cinza claro)
// = lida pelo outro lado.
const StatusIndicator = ({ read }: { read: boolean }) =>
  read ? (
    <CheckCheck className="h-3 w-3 text-white/80" aria-label="Lida" />
  ) : (
    <Check className="h-3 w-3 text-white/60" aria-label="Enviada" />
  );

const SelectionCheckmark = ({ selected }: { selected: boolean }) => (
  <div
    aria-hidden
    className={cn(
      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
      selected ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white',
    )}
  >
    {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
  </div>
);

const MessageBubbleImpl = ({
  message,
  isOwn,
  onOpenMedia,
  selectionMode = false,
  isSelected = false,
  onLongPress,
  onSelectToggle,
}: MessageBubbleProps) => {
  const time = formatTime(message.createdAt);

  // ===== Long-press / selection handlers =====
  // Usamos Pointer Events: cobre touch, mouse e caneta com a mesma API,
  // evitando duplicar handlers de touch + click. O timer eh limpo em
  // pointerup/pointercancel/pointermove (se mover alem da tolerancia o
  // gesto vira scroll e cancelamos o long-press).
  const pressTimer = useRef<number | null>(null);
  const pressOrigin = useRef<{ x: number; y: number } | null>(null);
  const longPressFiredRef = useRef(false);

  const clearPress = useCallback(() => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    pressOrigin.current = null;
  }, []);

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // So gesto primario (left-click ou touch dedo primario).
      if (e.button !== 0 && e.pointerType !== 'touch') return;
      longPressFiredRef.current = false;
      pressOrigin.current = { x: e.clientX, y: e.clientY };
      pressTimer.current = window.setTimeout(() => {
        longPressFiredRef.current = true;
        onLongPress?.(message.id);
      }, LONG_PRESS_MS);
    },
    [message.id, onLongPress],
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!pressOrigin.current) return;
      const dx = Math.abs(e.clientX - pressOrigin.current.x);
      const dy = Math.abs(e.clientY - pressOrigin.current.y);
      if (dx > LONG_PRESS_MOVE_TOLERANCE_PX || dy > LONG_PRESS_MOVE_TOLERANCE_PX) {
        clearPress();
      }
    },
    [clearPress],
  );

  const handlePointerUp = useCallback(() => {
    clearPress();
  }, [clearPress]);

  const handlePointerCancel = useCallback(() => {
    clearPress();
  }, [clearPress]);

  const handleContextMenu = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      // Atalho desktop equivalente ao long-press de mobile.
      e.preventDefault();
      if (!selectionMode) onLongPress?.(message.id);
    },
    [message.id, onLongPress, selectionMode],
  );

  const handleArticleClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (!selectionMode) return;
      // Se acabamos de disparar long-press, ignoramos o click sintetico
      // que segue (evita "selecionar + deselecionar" no mesmo gesto).
      if (longPressFiredRef.current) {
        longPressFiredRef.current = false;
        e.stopPropagation();
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      onSelectToggle?.(message.id);
    },
    [message.id, onSelectToggle, selectionMode],
  );

  const handleMediaClick = useCallback(
    (
      url: string,
      e: ReactMouseEvent<HTMLButtonElement>,
    ) => {
      if (selectionMode) {
        // Em selection mode o tap na imagem entra na selecao, nao abre
        // o lightbox.
        e.stopPropagation();
        e.preventDefault();
        onSelectToggle?.(message.id);
        return;
      }
      if (!onOpenMedia) return;
      onOpenMedia({
        url,
        name: message.fileName ?? null,
        type: message.fileType ?? null,
      });
    },
    [message.fileName, message.fileType, message.id, onOpenMedia, onSelectToggle, selectionMode],
  );

  const wrapper = cn(
    'group/bubble flex w-full select-none gap-2 transition-colors',
    selectionMode ? 'cursor-pointer rounded-xl px-2 py-1 -mx-2' : '',
    selectionMode && isSelected ? 'bg-blue-50/60' : '',
    isOwn ? 'justify-end' : 'justify-start',
  );

  const wrapperHandlers = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onPointerLeave: handlePointerCancel,
    onContextMenu: handleContextMenu,
    onClick: handleArticleClick,
  } as const;

  // ===== Placeholder de mensagem apagada =====
  if (message.isDeleted) {
    return (
      <article
        className={wrapper}
        aria-label="Mensagem apagada"
        {...wrapperHandlers}
      >
        {selectionMode && isOwn ? null : selectionMode ? <SelectionCheckmark selected={isSelected} /> : null}
        <div
          className={cn(
            'inline-flex max-w-[75%] items-center gap-2 rounded-2xl bg-gray-100 px-3.5 py-2',
            'text-sm italic text-gray-500',
            isOwn ? 'rounded-br-sm' : 'rounded-bl-sm',
          )}
        >
          <Ban className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Esta mensagem foi apagada</span>
          <span className="ml-1 text-[10px] not-italic text-gray-400">{time}</span>
        </div>
        {selectionMode && isOwn ? <SelectionCheckmark selected={isSelected} /> : null}
      </article>
    );
  }

  // Bubble base com rabicho no canto correspondente (top-right reto pro
  // proprio, top-left reto pro outro). Padding generoso, fonte legivel,
  // shadow sutil pra dar profundidade.
  const baseBubble = cn(
    'relative max-w-[75%] px-3.5 py-2 text-sm leading-relaxed shadow-[0_1px_1px_rgba(16,24,40,0.06)]',
    isOwn
      ? 'rounded-2xl rounded-br-sm bg-gray-900 text-white'
      : 'rounded-2xl rounded-bl-sm bg-white text-gray-900 ring-1 ring-gray-100',
  );

  const metaText = cn(
    'inline-flex items-center gap-1 text-[10px] leading-none',
    isOwn ? 'text-white/70' : 'text-gray-400',
  );

  const selectionLeading = selectionMode && !isOwn ? <SelectionCheckmark selected={isSelected} /> : null;
  const selectionTrailing = selectionMode && isOwn ? <SelectionCheckmark selected={isSelected} /> : null;

  if (message.type === 'CTA_REUNIAO') {
    const data = parseJson<CtaReuniao>(message.content);
    return (
      <article
        className={wrapper}
        aria-label={`Mensagem ${isOwn ? 'enviada' : 'recebida'} às ${time}`}
        {...wrapperHandlers}
      >
        {selectionLeading}
        <div className={cn(baseBubble, 'flex flex-col gap-2 px-4 py-3')}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
            <Calendar className="h-3.5 w-3.5" />
            Proposta de Reunião
          </div>
          <p className="text-sm font-medium">{data?.titulo ?? message.content}</p>
          {data ? (
            <div className="text-xs opacity-90">
              {data.data} · {data.hora} · {data.local}
            </div>
          ) : null}
          <div className="mt-1 flex gap-2 text-xs">
            <span className={cn('rounded-full px-2.5 py-1 font-medium', isOwn ? 'bg-white/20' : 'bg-gray-100')}>
              Aceitar
            </span>
            <span className={cn('rounded-full px-2.5 py-1', isOwn ? 'bg-white/10 opacity-80' : 'ring-1 ring-gray-200')}>
              Sugerir outro horário
            </span>
          </div>
          <div className={cn(metaText, 'mt-0.5 self-end')}>
            <span>{time}</span>
            {isOwn ? <StatusIndicator read={message.read} /> : null}
          </div>
        </div>
        {selectionTrailing}
      </article>
    );
  }

  if (message.type === 'CTA_VALOR') {
    const data = parseJson<CtaValor>(message.content);
    return (
      <article
        className={wrapper}
        aria-label={`Mensagem ${isOwn ? 'enviada' : 'recebida'} às ${time}`}
        {...wrapperHandlers}
      >
        {selectionLeading}
        <div className={cn(baseBubble, 'flex flex-col gap-1.5 px-4 py-3')}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
            <DollarSign className="h-3.5 w-3.5" />
            Valor proposto
          </div>
          <p className="text-lg font-semibold">{data ? formatBRL(data.valor) : message.content}</p>
          {data?.descricao ? <p className="text-xs opacity-90">{data.descricao}</p> : null}
          <div className={cn(metaText, 'mt-0.5 self-end')}>
            <span>{time}</span>
            {isOwn ? <StatusIndicator read={message.read} /> : null}
          </div>
        </div>
        {selectionTrailing}
      </article>
    );
  }

  if (message.type === 'FILE') {
    const absUrl = absoluteFileUrl(message.fileUrl);
    const isImage = (message.fileType ?? '').startsWith('image/');

    if (absUrl && isImage) {
      // Imagem: bubble compacto, foto domina o espaco. Hora flutua no
      // canto inferior direito da imagem com gradient pra legibilidade.
      return (
        <article
          className={wrapper}
          aria-label={`Imagem ${isOwn ? 'enviada' : 'recebida'} às ${time}`}
          {...wrapperHandlers}
        >
          {selectionLeading}
          <div className={cn(baseBubble, 'overflow-hidden p-1')}>
            <button
              type="button"
              onClick={(e) => handleMediaClick(absUrl, e)}
              aria-label={`Abrir ${message.fileName ?? 'imagem'}`}
              className="group relative block overflow-hidden rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
            >
              <img
                src={absUrl}
                alt={message.fileName ?? 'imagem'}
                className="block max-h-80 w-full cursor-zoom-in object-cover"
                loading="lazy"
                draggable={false}
              />
              {message.content ? (
                <div className="px-2.5 pt-1.5 text-sm">{message.content}</div>
              ) : null}
              <div className="pointer-events-none absolute bottom-1 right-2 flex items-center gap-1 rounded-full bg-black/40 px-2 py-0.5 text-[10px] text-white backdrop-blur-sm">
                <span>{time}</span>
                {isOwn ? <StatusIndicator read={message.read} /> : null}
              </div>
            </button>
          </div>
          {selectionTrailing}
        </article>
      );
    }

    return (
      <article
        className={wrapper}
        aria-label={`Arquivo ${isOwn ? 'enviado' : 'recebido'} às ${time}`}
        {...wrapperHandlers}
      >
        {selectionLeading}
        <div className={cn(baseBubble, 'flex max-w-[80%] flex-col gap-2 px-3 py-2.5')}>
          {absUrl ? (
            <button
              type="button"
              onClick={(e) => handleMediaClick(absUrl, e)}
              aria-label={`Abrir ${message.fileName ?? 'Arquivo'}`}
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-medium transition-colors',
                isOwn ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-50 hover:bg-gray-100',
              )}
            >
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate">{message.fileName ?? 'Arquivo'}</span>
            </button>
          ) : null}
          {message.content ? <p className="text-sm">{message.content}</p> : null}
          <div className={cn(metaText, 'self-end')}>
            <span>{time}</span>
            {isOwn ? <StatusIndicator read={message.read} /> : null}
          </div>
        </div>
        {selectionTrailing}
      </article>
    );
  }

  if (message.type === 'CTA_PROXIMO_PASSO') {
    const data = parseJson<CtaProximo>(message.content);
    return (
      <article
        className={wrapper}
        aria-label={`Mensagem ${isOwn ? 'enviada' : 'recebida'} às ${time}`}
        {...wrapperHandlers}
      >
        {selectionLeading}
        <div className={cn(baseBubble, 'flex flex-col gap-1.5 px-4 py-3')}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
            <ListChecks className="h-3.5 w-3.5" />
            Próximo passo
          </div>
          <p className="text-sm font-medium">{data?.descricao ?? message.content}</p>
          {data?.dueDate ? <p className="text-xs opacity-90">Até {data.dueDate}</p> : null}
          <div className={cn(metaText, 'mt-0.5 self-end')}>
            <span>{time}</span>
            {isOwn ? <StatusIndicator read={message.read} /> : null}
          </div>
        </div>
        {selectionTrailing}
      </article>
    );
  }

  // TEXT padrao.
  return (
    <article
      className={wrapper}
      aria-label={`Mensagem ${isOwn ? 'enviada' : 'recebida'} às ${time}`}
      {...wrapperHandlers}
    >
      {selectionLeading}
      <div className={baseBubble}>
        <p className="whitespace-pre-wrap break-words pr-12">{message.content}</p>
        <div className={cn(metaText, 'absolute bottom-1.5 right-2.5')}>
          <span>{time}</span>
          {isOwn ? <StatusIndicator read={message.read} /> : null}
        </div>
      </div>
      {selectionTrailing}
    </article>
  );
};

export const MessageBubble = memo(MessageBubbleImpl);
